import React from 'react'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ItemLoader, {RendererProps} from 'components/ItemLoader'
import Page from 'components/Page'
import type {Alert as AlertType, Trigger} from '@equistamp/types'
import Path from 'routeLinks'
import {
  alertCooldowns,
  alertTriggerAvailabilityMetrics,
  alertTriggerModelChanges,
  alertTriggerStabilityMetrics,
  alertTriggerThresholdMetrics,
  alertTriggerTopMetrics,
  alertTriggerTypes,
} from 'components/filters/constants'
import {MInfoItem} from 'components/text'
import useUser from 'hooks/useUser'
import {
  EVAL_SCORE_THRESHOLD,
  MODEL_AVAILABILITY,
  MODEL_CHANGE,
  MODEL_RANK_CHANGE,
  MODEL_STABILITY,
  NEW_EVALUATION,
  NEW_MODEL,
  TOP_MODEL,
} from '@equistamp/constants'
import {HelpTooltip} from 'components/filters/widgets'
import {RoundedButton} from 'components/forms/inputs'
import useModels from 'hooks/useModels'

const triggerTypeMetrics = {
  [TOP_MODEL]: alertTriggerTopMetrics,
  [MODEL_RANK_CHANGE]: alertTriggerTopMetrics,
  [MODEL_AVAILABILITY]: alertTriggerAvailabilityMetrics,
  [MODEL_STABILITY]: alertTriggerStabilityMetrics,
  [NEW_MODEL]: {},
  [NEW_EVALUATION]: {},
  [MODEL_CHANGE]: alertTriggerModelChanges,
  [EVAL_SCORE_THRESHOLD]: alertTriggerThresholdMetrics,
}

const MetricDocs = ({trigger}: {trigger?: Trigger}) => {
  if (!trigger?.metric || !trigger?.type) return null

  const typeVals = triggerTypeMetrics[trigger.type as keyof typeof triggerTypeMetrics]
  const {doc, title} = typeVals[trigger.metric as keyof typeof typeVals] as {
    doc: string
    title: string
  }

  return (
    <MInfoItem title="Metric">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography>{title}</Typography>
        <HelpTooltip help={doc} />
      </Stack>
    </MInfoItem>
  )
}

const ShowModels = ({trigger}: {trigger?: Trigger}) => {
  const {models} = useModels()

  const SeeAll = () => <RoundedButton label="See all" sx={{width: 'fit-content'}} />

  if (!trigger) return null
  if (!Object.keys(trigger.models || {}).length) {
    return (
      <MInfoItem title="All models checked">
        <SeeAll />
      </MInfoItem>
    )
  }
  const modelIds = trigger.models?.models?.map((m) => m.id)
  return (
    <MInfoItem
      title={`Chosen models (${modelIds?.length || models.length} ${
        modelIds ? 'selected' : 'found'
      })`}
    >
      <SeeAll />
    </MInfoItem>
  )
}

const ShowAlert = ({item: alert, setItem: setAlert}: RendererProps<AlertType>) => {
  const {isAdmin} = useUser()
  const trigger = alert.triggers && alert.triggers[0]

  return (
    <Page
      title={alert.name}
      fullWidth
      breadcrumbs={[{title: 'alerts', link: Path.alerts.all()}, {title: alert.name}]}
    >
      <Grid container sx={{p: 8}}>
        <Grid item md={6}>
          <Stack spacing={3}>
            <Typography variant="h3" sx={{fontWeight: 600}}>
              {alert.name}
            </Typography>
            <Typography variant="h5" sx={{color: 'secondary.subText'}}>
              {trigger?.type && alertTriggerTypes[trigger.type]}
            </Typography>

            <RoundedButton
              label="Subscribe to this alert"
              sx={{width: 'fit-content', mt: 1, mb: 1}}
            />

            <MInfoItem title="Description" markdown>
              {alert.description}
            </MInfoItem>
            <MInfoItem title="Visibility">{alert.public ? 'Public' : 'Private'}</MInfoItem>

            {isAdmin && (
              <MInfoItem title="Repeat rate">
                {alertCooldowns[alert.trigger_cooldown || 'always']}
              </MInfoItem>
            )}

            <MetricDocs trigger={trigger} />

            <ShowModels trigger={trigger} />
          </Stack>
        </Grid>
      </Grid>
      <Stack direction="column" spacing={7} sx={{m: 5}}></Stack>
    </Page>
  )
}

const Alert: React.FC = () => <ItemLoader itemName="alert" renderer={ShowAlert} />
export default Alert
